import { html } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_EXCLUSIONS_FORM } from '../../../../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../../../../utils/translator.helper';
import { styles } from '../view/styles';
import { GzFormFieldWebComponent } from '../../../../../../../../../../ui/gzForm/formField';
import { GzInputWebComponent } from '../../../../../../../../../../ui/gzInput/component/GzInputWebComponent';
import { RequiredValidator } from '../../../../../../../../../../ui/gzForm/validators/required.validator';
import { FormGroup } from '../../../../../../../../../../ui/gzForm/formGroup/form-group';
import { InputWidthValues } from '../../../../../../../../../../ui/gzInput';
import { GzCheckboxWebComponent } from '../../../../../../../../../../ui/gzCheckbox';
import { GzPolicyFullPageWindowFormWebComponent } from '../../../../../../../../../common/policyPanel/uiSettings/dialogs/fullPage/form';
import { GzDropdownWebComponent } from '../../../../../../../../../../ui/gzDropdown';
import { toggleVisibility } from '../../../../../../../../../../utils/htmlElement.utils';
import { ExchangeAntimalwareExclusionFormValue } from '../../../../../../../utils/uiSettings/exchange/exchange-protection.types';
import { getExchangeApplyToRuleFormOptions } from '../../../../../../../utils/uiSettings/exchange/exchange-protection.values';
import {
  ExchangeRuleScopeApplyToType,
  ExchangeRuleScopeEmailSelection,
} from '@nirvana/shared/dtos/types/policies/common';
import { GzRadioGroupWebComponent } from '../../../../../../../../../../ui/gzRadio';
import { GzExchangeUserGroupsGridWebComponent } from '../../../../../helpers/gridComponents/userGroupsWithSelection/GzExchangeUserGroupsGridWebComponent';
import { CustomizableValidator } from '../../../../../../../../../../ui/gzForm/validators/customizable.validator';
import { ruleScopeSpecificValidators, validateScopeGroup } from '../utils/validators';
import _ from 'lodash';
import { FontSizes, FontWeights } from 'apps/webcomponents/src/types/common/types';

@customElement(WEB_COMPONENT_POLICY_PANEL_EXCHANGE_ANTIMALWARE_EXCLUSIONS_FORM)
export class GzExchangeAntimalwareExclusionsFormWebComponent extends GzPolicyFullPageWindowFormWebComponent<ExchangeAntimalwareExclusionFormValue> {
  // general section
  @query('#name')
  ruleName: GzFormFieldWebComponent<GzInputWebComponent, string>;
  @query('#active-form-field')
  private activeFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  // scope section
  @query('#rule-scope-apply-to-field')
  private ruleScopeApplyToField: GzFormFieldWebComponent<GzDropdownWebComponent>;
  @query('#rule-scope-from-field')
  private ruleScopeFromField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-scope-from-specific-field')
  private ruleScopeFromSpecificField: GzFormFieldWebComponent<GzExchangeUserGroupsGridWebComponent>;
  @query('#rule-scope-to-field')
  private ruleScopeToField: GzFormFieldWebComponent<GzRadioGroupWebComponent>;
  @query('#rule-scope-to-specific-field')
  private ruleScopeToSpecificField: GzFormFieldWebComponent<GzExchangeUserGroupsGridWebComponent>;
  @query('#rule-scope-match-all-recipients-field')
  private ruleScopeMatchAllRecipientsField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  public constructor(value?: ExchangeAntimalwareExclusionFormValue) {
    super(value);
  }

  static get styles() {
    return styles;
  }

  public setError(error: string) {
    this.ruleName.setError(error);
  }

  protected getConvertedFormValue() {
    return _.cloneDeep(this.formGroup.value);
  }

  createFormGroup(): void {
    this.ruleName.Validators = [RequiredValidator(translateFn('Required field'))];

    this.ruleScopeFromField.Validators = [
      CustomizableValidator(() =>
        validateScopeGroup(
          this.ruleScopeFromField.value as ExchangeRuleScopeEmailSelection,
          this.ruleScopeToField.value as ExchangeRuleScopeEmailSelection
        )
      ),
    ];
    this.ruleScopeToField.Validators = [
      CustomizableValidator(() =>
        validateScopeGroup(
          this.ruleScopeFromField.value as ExchangeRuleScopeEmailSelection,
          this.ruleScopeToField.value as ExchangeRuleScopeEmailSelection
        )
      ),
    ];

    this.ruleScopeToSpecificField.touched = true;
    this.ruleScopeToSpecificField.Validators = ruleScopeSpecificValidators();
    this.ruleScopeFromSpecificField.touched = true;
    this.ruleScopeFromSpecificField.Validators = ruleScopeSpecificValidators();

    this.formGroup = FormGroup.build({
      name: this.ruleName,
      active: this.activeFormField,
      scope: {
        applyTo: this.ruleScopeApplyToField,
        from: this.ruleScopeFromField,
        to: this.ruleScopeToField,
        fromSpecific: this.ruleScopeFromSpecificField,
        toSpecific: this.ruleScopeToSpecificField,
        matchAllRecipients: this.ruleScopeMatchAllRecipientsField,
      },
    });
  }

  setFormGroupValue() {
    if (!this.value) {
      return;
    }

    this.formGroup.value = {
      name: this.value.name,
      active: this.value.active,
      scope: {
        applyTo: this.value.scope.applyTo,
        from: this.value.scope.from,
        to: this.value.scope.to,
        fromSpecific: this.value.scope.fromSpecific,
        toSpecific: this.value.scope.toSpecific,
        matchAllRecipients: this.value.scope.matchAllRecipients,
      },
    };
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div class="rules-form-container">
        <!-- General section -->
        <div id="rules-general-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle">${translateFn('General')}</gz-title>
          <gz-form-field id="name" class="label-mw-250" label="${translateFn('Exclusion name')}" required>
            <gz-input
              inputWidth="${InputWidthValues.LARGE}"
              gz-form-input
              placeholder="${translateFn('Enter the exclusion name')}"
            ></gz-input>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="active-form-field" class="mt-2">
            <gz-checkbox gz-form-input label="${translateFn('Active')}" checked></gz-checkbox>
          </gz-form-field>
        </div>

        <!-- Rule scope section -->
        <div id="rules-scope-section">
          <gz-title fontWeight="500" fontSize="18" class="module-subtitle mt-5">${translateFn('Rule scope')}</gz-title>
          <gz-form-field id="rule-scope-apply-to-field" label="${translateFn('Apply to (direction)')}">
            <gz-dropdown
              gz-form-input
              .options=${getExchangeApplyToRuleFormOptions()}
              .value=${ExchangeRuleScopeApplyToType.ALL}
            ></gz-dropdown>
          </gz-form-field>

          <gz-labeled-content
            hasFormLabelWidth
            hasColon
            labelFontSize="${FontSizes.HEADING_6}"
            labelFontWeight="${FontWeights.NORMAL}"
            label="${translateFn('Senders')}"
          >
            <gz-form-field id="rule-scope-from-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                @radioSelectionChanged=${this.handleScopeFromEmailSelectionChange.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Any')}"
                  .value=${ExchangeRuleScopeEmailSelection.ANY}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('Specific')}"
                  .value=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </gz-labeled-content>
          <gz-text
            id="rule-scope-from-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'By default, the rule applies to all senders. To apply different filtering settings and actions for certain sender groups, select {bold}Specific{/bold} and add a custom rule.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-scope-from-specific-field">
            <gz-policy-panel-exchange-user-groups-grid gz-form-input></gz-policy-panel-exchange-user-groups-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-labeled-content
            hasFormLabelWidth
            hasColon
            labelFontSize="${FontSizes.HEADING_6}"
            labelFontWeight="${FontWeights.NORMAL}"
            label="${translateFn('Recipients')}"
            class="mt-2"
          >
            <gz-form-field id="rule-scope-to-field">
              <gz-radio-group
                gz-form-input
                .selectedOption=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                @radioSelectionChanged=${this.handleScopeToEmailSelectionChange.bind(this)}
              >
                <gz-radio
                  class="mr-7"
                  label="${translateFn('Any')}"
                  .value=${ExchangeRuleScopeEmailSelection.ANY}
                ></gz-radio>
                <gz-radio
                  label="${translateFn('Specific')}"
                  .value=${ExchangeRuleScopeEmailSelection.SPECIFIC}
                ></gz-radio>
              </gz-radio-group>
              <gz-input-error></gz-input-error>
            </gz-form-field>
          </gz-labeled-content>
          <gz-text
            id="rule-scope-to-description"
            fontSize="${FontSizes.PARAGRAPH_12}"
            fontWeight="${FontWeights.NORMAL}"
            class="infoText"
            .innerHTML=${translateFn(
              'By default, the rule applies to all recipients. To apply different filtering settings and actions for certain sender groups, select {bold}Specific{/bold} and add a custom rule.',
              {
                bold: '<strong>',
                '/bold': '</strong>',
              }
            )}
          ></gz-text>

          <gz-form-field id="rule-scope-to-specific-field" class="mt-2">
            <gz-policy-panel-exchange-user-groups-grid gz-form-input></gz-policy-panel-exchange-user-groups-grid>
            <gz-input-error></gz-input-error>
          </gz-form-field>

          <gz-form-field id="rule-scope-match-all-recipients-field" class="pb-2">
            <gz-checkbox label="${translateFn('Match all recipients')}" gz-form-input></gz-checkbox>
          </gz-form-field>
        </div>
      </div>
    `;
  }

  private handleScopeFromEmailSelectionChange(event: CustomEvent) {
    const isSpecific = event.detail.selectedValue === ExchangeRuleScopeEmailSelection.SPECIFIC;

    // show/hide user groups grid
    toggleVisibility(this.ruleScopeFromSpecificField, isSpecific);
    this.ruleScopeFromSpecificField.Validators = isSpecific ? ruleScopeSpecificValidators() : [];

    // validate the other field
    this.ruleScopeToField.validate(true);
  }

  private handleScopeToEmailSelectionChange(event: CustomEvent) {
    const isSpecific = event.detail.selectedValue === ExchangeRuleScopeEmailSelection.SPECIFIC;

    // show/hide user groups grid
    toggleVisibility(this.ruleScopeToSpecificField, isSpecific);
    this.ruleScopeToSpecificField.Validators = isSpecific ? ruleScopeSpecificValidators() : [];

    // show/hide match all recipients
    toggleVisibility(this.ruleScopeMatchAllRecipientsField, isSpecific);

    // validate the other field
    this.ruleScopeFromField.validate(true);
  }
}
